import React, {
    ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef,
    useState,
} from "react";
import Styles from "./ScrollableList.module.scss";
import Text from "../theme/text/Text";
import { LoadingIcon } from "../loading/Loading";
import useChangingCallback from "../../use-changing-callback";

export interface ScrollableListProps {
    children: ReactNode;
    className?: string;
    onScroll?: () => (unknown | Promise<unknown>);
    isFragment?: boolean;
    threshold?: number;
    loadingText?: ReactNode;
}

export function getScrollParent(element: HTMLElement): HTMLElement | undefined {
    if (element === document.body) {
        return undefined;
    }

    const style = window.getComputedStyle(element);

    if (style.overflowY === "scroll"
        || style.overflowY === "auto") {
        return element;
    }

    return element.parentElement ? getScrollParent(element.parentElement) : undefined;
}

export default function ScrollableList(props: ScrollableListProps): ReactElement {
    const loader = useRef<HTMLDivElement | null>(null);

    const [isScrolling, setIsScrolling] = useState(false);

    const handleObserver = useChangingCallback(async (entries: IntersectionObserverEntry[]) => {
        const target = entries[0];
        if (target.isIntersecting && !isScrolling) {
            setIsScrolling(true);
            await props.onScroll?.();
            const parent = getScrollParent(loader.current as HTMLElement);
            parent?.scrollTo({
                top: parent.scrollTop - 5,
            });
            setIsScrolling(false);
        }
    }, [props.onScroll, isScrolling]);

    const loadingView = useMemo(() => {
        if (!props.onScroll || !props.loadingText) {
            return undefined;
        }
        return (
            <Text textVariant="body-2-regular" colorVariant="body-secondary">
                <LoadingIcon size="10px" />
                {" "}
                {props.loadingText}
            </Text>
        );
    }, [props.onScroll, props.loadingText]);

    useEffect(() => {
        if (!props.onScroll) {
            return undefined;
        }
        const option = {
            root: null,
            rootMargin: "0px",
            threshold: props.threshold,
        };

        const observer = new IntersectionObserver(handleObserver, option);

        if (loader.current) {
            observer.observe(loader.current);
        }

        return () => {
            if (loader.current) {
                observer.unobserve(loader.current as Element);
            }
        };
    }, [handleObserver, props.onScroll]);

    if (props.isFragment) {
        return (
            <>
                {props.children}
                {props.onScroll && (
                    <div className={Styles.loading} ref={loader}>
                        {isScrolling && loadingView}
                    </div>
                )}
            </>
        );
    }

    return (
        <div className={props.className}>
            <div>
                {props.children}
            </div>
            {props.onScroll && (
                <div className={Styles.loading} ref={loader}>
                    {isScrolling && loadingView}
                </div>
            )}
        </div>
    );
}

ScrollableList.defaultProps = {
    className: undefined,
    isFragment: false,
    threshold: 1.0,
    onScroll: undefined,
    loadingText: undefined,
};
